import React from "react";
import styles from "./LoginRemindPopup.module.less";
import useDialogManager from "@/utils/useDialogManager";
import { getCountryAsset } from "@/utils/countryAssetUtils";
import { useTranslationPlus } from "@/Core/i18n/useTranslationPlus";
import {
  getCurrentInstallLink,
  shouldShowInstallTopBanner,
} from "@/utils/countryConfigUtils";
import { openInstallLinkWithApkTransfer } from "@/features/install/apkTransfer";
import { INSTALL_TRACK_SOURCES } from "@/features/analytics/em/events";
import useScreenDesign from "@/Core/utils/hooks/useScreenDesign";
import { shouldShowMobileInstallEntry } from "@/features/install/visibility";
import MarketingImagePopup from "@/features/common/marketingPopup";
import {
  trackInstallEntryClose,
  useTrackInstallEntryView,
} from "@/features/install/installEntryAnalytics";

interface LoginRemindPopupProps {
  onClose: () => void;
  onActionClick?: () => void;
}

/**
 * 未登录提醒弹窗组件
 * 当用户未登录时显示，提示用户注册或登录
 */
const LoginRemindPopup: React.FC<LoginRemindPopupProps> = ({
  onClose,
  onActionClick,
}) => {
  const { openSignUp } = useDialogManager();
  const { t } = useTranslationPlus("install");
  const isMobile = useScreenDesign();
  const popup = getCountryAsset("loginPopup");
  const shouldShowDownloadCta = shouldShowMobileInstallEntry({
    isMobile,
    canShowInstallEntry: shouldShowInstallTopBanner(),
  });

  useTrackInstallEntryView({
    visible: shouldShowDownloadCta,
    source: INSTALL_TRACK_SOURCES.LOGIN_REMIND_POPUP,
    closable: true,
  });

  // 处理图片点击
  const handleImageClick = () => {
    if (onActionClick) {
      onActionClick();
    }
    onClose();
    // Let the popup unmount before opening DialogPlus, otherwise the two
    // overlay systems can compete for the same frame and cause a flash.
    window.requestAnimationFrame(() => {
      openSignUp();
    });
  };

  const handleDownloadClick = () => {
    const installLink = getCurrentInstallLink();
    if (!installLink) {
      return;
    }

    void openInstallLinkWithApkTransfer(installLink, {
      source: INSTALL_TRACK_SOURCES.LOGIN_REMIND_POPUP,
    });
  };

  const handlePopupClose = () => {
    if (shouldShowDownloadCta) {
      trackInstallEntryClose(INSTALL_TRACK_SOURCES.LOGIN_REMIND_POPUP);
    }
    onClose();
  };

  return (
    <MarketingImagePopup
      imageSrc={popup}
      imageAlt="login reminder"
      onImageClick={handleImageClick}
      onClose={handlePopupClose}
      imageClassName={styles.popupImage}
      footer={
        shouldShowDownloadCta ? (
          <button
            type="button"
            className={styles.downloadCta}
            onClick={handleDownloadClick}
          >
            {t("loginRemindPopupCta")}
          </button>
        ) : null
      }
    />
  );
};

export default LoginRemindPopup;
